import { getStatusAtivoLabelPorSlug } from '../../../modules/types/StatusAtivoEnum';
import { EnumOption, ProaqRelatedData } from '../../../modules/types/types';
import { getAllProaqfases } from '../../../services/processosAquisitivos/proaqFaseService';
import { getAllProaqItens } from '../../../services/processosAquisitivos/proaqItemService';
import { getAllProaqPrecoCMED } from '../../../services/processosAquisitivos/proaqPrecoCMEDService';
import { formatValorDinheiro } from '../../functions';
import { ExportSheet } from '../core';
import { baseLogColumns, DataPreparer } from '../factory';
import {
  addResponsaveisAquisicaoSheet,
  addTramitacoesSeiSheet,
  fetchGenericEntityData,
} from './auxiliar_componentes';

export class ProaqExportPreparer implements DataPreparer {
  private modulo = 'proaq';
  /**
   * Busca todos os dados relacionados aos PROAQs selecionados
   * Esta função demonstra como usar tanto os services específicos quanto o padrão genérico
   */
  private async fetchRelatedData(
    proaqIds: number[],
  ): Promise<ProaqRelatedData> {
    if (proaqIds.length === 0) return {};

    try {
      const [allFases, allItens, allPrecos] = await Promise.all([
        this.fetchProaqFases(),
        this.fetchProaqItens(),
        this.fetchProaqPrecos(),
      ]);

      const fases = this.filterByProaqIds(allFases, proaqIds);
      const itens = this.filterByProaqIds(allItens, proaqIds);
      const precos = this.filterByProaqIds(allPrecos, proaqIds);

      const [responsaveisAquisicao, tramitacoesSei] = await Promise.all([
        fetchGenericEntityData('responsavelaquisicao', this.modulo, proaqIds),
        fetchGenericEntityData('tramitacoes', this.modulo, proaqIds),
      ]);

      return {
        fases,
        itens,
        precos,
        responsaveisAquisicao,
        tramitacoesSei,
      };
    } catch (error) {
      console.error('Erro ao buscar dados relacionados do Proaq:', error);
      return {};
    }
  }

  private async fetchProaqFases(): Promise<any[]> {
    try {
      const result = await getAllProaqfases();
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error('Erro ao buscar fases do Proaq:', error);
      return [];
    }
  }

  private async fetchProaqItens(): Promise<any[]> {
    try {
      const result = await getAllProaqItens();
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error('Erro ao buscar itens do Proaq:', error);
      return [];
    }
  }

  private async fetchProaqPrecos(): Promise<any[]> {
    try {
      const result = await getAllProaqPrecoCMED();
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error('Erro ao buscar fases do Proaq:', error);
      return [];
    }
  }

  private filterByProaqIds(data: any[], proaqIds: number[]): any[] {
    if (!Array.isArray(data) || data.length === 0) return [];

    return data.filter((item) => {
      // Acessa o id do Proaq através de duas formas possíveis
      const proaqId = item.proaq?.id || item.proaqId;
      return proaqIds.includes(proaqId);
    });
  }

  async prepareForExport(
    selectedProaqs: any[],
    additionalData?: {
      TipoItemAquisitivo?: EnumOption[];
      componenteAF?: EnumOption[];
      ProaqStatus?: EnumOption[];
      TipoCota?: EnumOption[];
    },
  ): Promise<ExportSheet[]> {
    const sheets: ExportSheet[] = [];
    const proaqIds = selectedProaqs.map((p) => p.id);

    const {
      TipoItemAquisitivo = [],
      componenteAF = [],
      ProaqStatus = [],
      TipoCota = [],
    } = additionalData || {};

    // 1. Sheet principal - Processos Aquisitivos (sempre presente)
    sheets.push({
      name: 'Processos Aquisitivos',
      data: selectedProaqs.map((proaq) =>
        this.transformProaq(
          proaq,
          componenteAF,
          TipoItemAquisitivo,
          ProaqStatus,
        ),
      ),
      columns: {
        ...baseLogColumns,
        modalidadeAquisicao: 'Modalidade de Aquisição',
        subtipoModalidade: 'Subtipo da Modalidade',
        tipoItemAquisitivo: 'Tipo de Item',
        unidadeDAF: 'Unidade DAF',
        produtoDAF: 'Produto Farmacêutico',
        denominacaoOuKit: 'Denominação Genérica / Kit de Produto',
        denominacaoGenerica: 'Denominação Genérica',
        KitProdutoDAF: 'Kit de Produtos',
        numeroSeiProaq: 'Número SEI',
        numeroEtp: 'Número ETP',
        componenteAF: 'Componente AF',
        proaqResponsavel: 'Responsável pelo Processo',
      },
    });

    // 2. Buscar dados relacionados
    const relatedData = await this.fetchRelatedData(proaqIds);

    // 3. Adicionar sheets dos dados relacionados (apenas se houver dados)
    this.addFasesSheet(sheets, relatedData.fases);
    this.addItensSheet(sheets, TipoCota, relatedData.itens);
    this.addPrecosSheet(sheets, relatedData.precos);
    addResponsaveisAquisicaoSheet(
      sheets,
      this.modulo,
      relatedData.responsaveisAquisicao,
    );
    addTramitacoesSeiSheet(sheets, this.modulo, relatedData.tramitacoesSei);

    return sheets;
  }

  /**
   * Transformação do objeto Proaq principal
   */
  private transformProaq = (
    proaq: any,
    componenteAF: EnumOption[],
    TipoItemAquisitivo: EnumOption[],
    ProaqStatus: EnumOption[],
  ) => {
    return {
      //logs
      id: proaq.id,
      usuarioRegistro: proaq.usuarioRegistro?.nomeCompleto || 'N/A',
      dataRegistro: proaq.dataRegistro,
      dataUltimaAtualizacao: proaq.dataUltimaAtualizacao,
      usuarioAtualizacao: proaq.usuarioAtualizacao?.nomeCompleto || 'N/A',
      nEdicoes: proaq.nEdicoes || 0,
      nVisualizacoes: proaq.nVisualizacoes || 0,
      nVisualizadores: proaq.nVisualizadores || 0,

      //módulo
      status: ProaqStatus[proaq.status]?.label || 'N/A',
      numeroSeiProaq: proaq.numeroSeiProaq || 'N/A',
      numeroEtp: proaq.numeroEtp || 'N/A',
      componenteAF: proaq.componenteAF
        ? componenteAF[proaq.componenteAF]?.label
        : 'N/A',
      modalidadeAquisicao:
        proaq.__tipoModalidade__?.nomeTipoModalidade || 'N/A',
      tipoItemAquisitivo:
        TipoItemAquisitivo[proaq.tipoItemAquisitivo]?.label || 'N/A',
      denominacaoOuKit:
        proaq.denominacaoGenerica?.denominacaoGenerica ||
        proaq.KitProdutoDAF?.nomeKit ||
        'N/A',

      //observações
      observacoes: proaq.observacoes || 'Não informado',
    };
  };

  /**
   * Adiciona sheet de Fases se houver dados
   */
  private addFasesSheet(sheets: ExportSheet[], fases?: any[]) {
    if (!fases?.length) return;

    sheets.push({
      name: 'Fases',
      data: fases.map(this.transformFase),
      columns: {
        ...baseLogColumns,
        proaqId: 'ID PROAQ',
        proaq: 'ID do Processo Licitatório',
        fase: 'Fase',
        dataEntrada: 'Data Entrada',
        dataSaida: 'Data Saída',
        dataRegistro: 'Data Registro',
        observacoes: 'Observações',
      },
    });
  }

  private transformFase = (fase: any) => ({
    //logs
    id: fase.id,
    usuarioRegistro: fase.usuarioRegistro?.nomeCompleto || 'N/A',
    dataRegistro: fase.dataRegistro,
    dataUltimaAtualizacao: fase.dataUltimaAtualizacao,
    usuarioAtualizacao: fase.usuarioAtualizacao?.nomeCompleto || 'N/A',
    nEdicoes: fase.nEdicoes || 0,
    nVisualizacoes: fase.nVisualizacoes || 0,
    nVisualizadores: fase.nVisualizadores || 0,
    proaqId: fase.proaq?.id || fase.proaq_id,
    fase: fase.fluxoProaqFase?.nomeFase || 'Não iniciado',
    dataEntrada: fase.dataEntrada || 'Não iniciado',
    dataSaida: fase.dataSaida || 'Não informado',
    observacoes: fase.observacoes || 'Não informado',
  });

  /**
   * Adiciona sheet de Itens se houver dados
   */
  private addItensSheet(
    sheets: ExportSheet[],
    TipoCota: EnumOption[],
    itens?: any[],
  ) {
    if (!itens?.length) return;

    sheets.push({
      name: 'Itens',
      data: itens.map((item: any) => this.transformItem(item, TipoCota)),
      columns: {
        ...baseLogColumns,
        proaqId: 'ID PROAQ',
        numeroItem: 'Nº Item',
        tipoCota: 'Tipo Cota',
        cmmInicio: 'CMM Início',
        cmmFinal: 'CMM Final',
        cmmValorReferencia: 'CMM Valor Referência',
        qtdAdministrativa: 'Qtd Administrativa',
        qtdJudicial: 'Qtd Judicial',
        qtdEstrategico: 'Qtd Estratégico',
        qtdOutro: 'Qtd Outro',
        qtdTotal: 'Qtd Total',
        valorUntEmbalagem: 'Valor Unit. Embalagem',
        valorTotalEstimado: 'Valor Total Estimado',
        coberturaDias: 'Cobertura (dias)',
        coberturaMeses: 'Cobertura (meses)',
        observacoes: 'Observações',
      },
    });
  }

  private transformItem = (item: any, TipoCota: EnumOption[]) => ({
    //logs
    id: item.id,
    usuarioRegistro: item.usuarioRegistro?.nomeCompleto || 'N/A',
    dataRegistro: item.dataRegistro,
    dataUltimaAtualizacao: item.dataUltimaAtualizacao,
    usuarioAtualizacao: item.usuarioAtualizacao?.nomeCompleto || 'N/A',
    nEdicoes: item.nEdicoes || 0,
    nVisualizacoes: item.nVisualizacoes || 0,
    nVisualizadores: item.nVisualizadores || 0,
    proaqId: item.proaq?.id || item.proaq_id,
    numeroItem: item.numeroItem,
    tipoCota: item.tipoCota ? TipoCota[item.tipoCota]?.label : 'Não informado',
    cmmInicio: item.cmmInicio || 'Não informado',
    cmmFinal: item.cmmFinal || 'Não informado',
    cmmValorReferencia: item.cmmValorReferencia || 'Não informado',
    qtdAdministrativa: item.qtdAdministrativa || 0,
    qtdJudicial: item.qtdJudicial || 0,
    qtdEstrategico: item.qtdEstrategico || 0,
    qtdOutro: item.qtdOutro || 0,
    qtdTotal:
      parseInt(item.qtdAdministrativa || '0', 10) +
        parseInt(item.qtdJudicial || '0', 10) +
        parseInt(item.qtdEstrategico || '0', 10) +
        parseInt(item.qtdOutro || '0', 10) || 0,
    valorUntEmbalagem: item.valorUntEmbalagem || 'Não informado',
    valorTotalEstimado: item.valorTotalEstimado || 'Não informado',
    coberturaDias: item.coberturaDias || 'Não informado',
    coberturaMeses: item.coberturaMeses || 'Não informado',
    observacoes: item.observacoes || 'Não informado',
  });

  /**
   * Adiciona sheet de Preços se houver dados
   */
  private addPrecosSheet(sheets: ExportSheet[], precos?: any[]) {
    if (!precos?.length) return;

    sheets.push({
      name: 'Preços',
      data: precos.map(this.transformPreco),
      columns: {
        ...baseLogColumns,
        proaqId: 'ID PROAQ',
        produto: 'Produto Farmacêutico',
        fornecedor: 'Fornecedor',
        cnpj: 'CNPJ do Fornecedor',
        dataPreco: 'Data do Preço',
        preco: 'Preço',
        isCMED: 'Preço CMED',
        observacoes: 'Observações',
      },
    });
  }

  private transformPreco = (preco: any) => ({
    //logs
    id: preco.id,
    usuarioRegistro: preco.usuarioRegistro?.nomeCompleto || 'N/A',
    dataRegistro: preco.dataRegistro,
    dataUltimaAtualizacao: preco.dataUltimaAtualizacao,
    usuarioAtualizacao: preco.usuarioAtualizacao?.nomeCompleto || 'N/A',
    nEdicoes: preco.nEdicoes || 0,
    nVisualizacoes: preco.nVisualizacoes || 0,
    nVisualizadores: preco.nVisualizadores || 0,
    proaqId: preco.proaq?.id || preco.proaq_id,
    produto: preco.produtoDAF?.produtoFarmaceutico ?? 'Não informado',
    fornecedor: preco.fornecedor?.razaoSocial ?? 'Não informado',
    cnpj: preco.fornecedor?.cnpj ?? 'Não informado',
    dataPreco: preco.dataPreco || 'Não informado',
    isCMED: preco.isCMED
      ? getStatusAtivoLabelPorSlug(preco.isCMED.toString())
      : '-',
    preco: preco.preco
      ? `R$ ${formatValorDinheiro(parseFloat(preco.preco.toString()).toFixed(2))}`
      : '-',
    observacoes: preco.observacoes || 'Não informado',
  });
}
